import { ImageViewer } from '@euler/app/components/media/ImageViewer';
import { MediaCoverView } from '@euler/app/components/media/MediaCoverView';
import { MediaUploadStatusOverlay } from '@euler/app/components/media/UploadStatusOverlay';
import { Typography } from '@euler/app/components/typography';
import { MediaInfo } from '@euler/app/flows/task/functions/models';
import { StatusColors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { LayoutProviderView } from '@euler/components/layout/LayoutProvider';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { MediaObject } from '@euler/model';
import { withConfirmation } from '@euler/utils';
import { range } from 'ramda';
import { memo, useCallback, useMemo, useState } from 'react';
import {
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { useTheme } from 'styled-components';

const kDefaultNumberOfColumns = 3;
const kSpacing = 14;
const kVerticalSpacing = 10;
const kShowSuccessCheckmark = false;

type CellInfo =
  | { type: 'media'; media: MediaInfo; index: number }
  | { type: 'placeholder'; showRequired?: boolean };

const MediaView = memo(
  ({
    item,
    onPress,
  }: {
    item: MediaInfo;
    onPress?: (media: MediaObject) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const media = useMemo<MediaObject>(
      () => ({
        id: item.key,
        url: item.url,
        type: item.type,
        coverUrl: item.coverUrl,
      }),
      [item],
    );
    return (
      <MediaCoverView
        media={media}
        noPlaceholder
        playIconSize={rpx(28)}
        onPress={onPress}
        css={`
          border-radius: ${rpx(6)}px;
          overflow: hidden;
        `}
      />
    );
  },
);

const Cell = memo(
  ({
    cell,
    editable,
    size,
    style,
    onPress,
    onDelete,
    onEdit,
    onRetry,
  }: {
    cell: CellInfo;
    editable?: boolean;
    size: number;
    style?: StyleProp<ViewStyle>;
    onPress?: (cell: CellInfo) => void;
    onDelete?: (cell: CellInfo & { type: 'media' }) => void;
    onRetry?: (cell: CellInfo & { type: 'media' }) => void;
    onEdit?: (cell: CellInfo & { type: 'media' }) => void;
  }) => {
    const {
      colors: {
        status: { danger },
      },
      inspection: {
        siteInspection: {
          itemCard: { mediaPlaceholderBgColor, mediaPlaceholderIconColor },
        },
      },
    } = useTheme();

    const { rpx } = useResponsiveContext();

    const onMediaPress = useCallback(() => {
      onPress?.(cell);
    }, [cell, onPress]);

    const onDeletePress = useCallback(() => {
      if (cell.type === 'placeholder') return;
      onDelete?.(cell);
    }, [cell, onDelete]);

    const onEditPress = useCallback(() => {
      if (cell.type === 'placeholder') return;
      onEdit?.(cell);
    }, [cell, onEdit]);

    const onRetryPress = useCallback(async () => {
      if (cell.type === 'placeholder' || cell.media.status !== 'error') return;
      const result = await withConfirmation({
        title: '是否重新上传?',
        message: '',
        confirmButtonText: '是',
        cancelButtonText: '否',
      });
      if (result) {
        onRetry?.(cell);
      }
    }, [cell, onRetry]);

    const scale = size / rpx(113);

    if (cell.type === 'placeholder') {
      return (
        <TouchableOpacity
          onPress={onMediaPress}
          css={`
            width: ${size}px;
            height: ${size}px;
            background-color: ${mediaPlaceholderBgColor};
            border-radius: ${rpx(6)}px;
            align-items: center;
            justify-content: center;
          `}
          style={style}
        >
          {cell.showRequired && (
            <Typography.Label
              light
              size={Math.floor(12 * scale)}
              color={StatusColors.Danger}
              css={`
                margin-bottom: ${rpx(2)}px;
              `}
            >
              *需要拍照
            </Typography.Label>
          )}
          <Icon
            name="camera"
            size={Math.floor(30 * scale)}
            color={
              cell.showRequired
                ? StatusColors.Danger
                : mediaPlaceholderIconColor
            }
          />
        </TouchableOpacity>
      );
    }

    const { status } = cell.media;

    return (
      <View
        css={`
          width: ${size}px;
          height: ${size}px;
          border-radius: ${rpx(6)}px;
        `}
        style={style}
      >
        <MediaView item={cell.media} onPress={onMediaPress} />
        {editable && (
          <>
            {status != null && status !== 'uploaded' ? (
              <MediaUploadStatusOverlay
                status={status}
                showSuccessCheckmark={kShowSuccessCheckmark}
                progressViewSize={size / 2}
                progress={cell.media.progress}
                onRetry={onRetryPress}
                style={StyleSheet.absoluteFill}
                css={`
                  border-radius: ${rpx(6)}px;
                `}
              />
            ) : cell.media.type === 'image' && onEdit != null ? (
              <TouchableOpacity
                css={`
                  background-color: rgba(0, 0, 0, 0.6);
                  border-bottom-right-radius: ${rpx(6)}px;
                  border-top-left-radius: ${rpx(6)}px;
                  width: ${rpx(22)}px;
                  height: ${rpx(22)}px;
                  position: absolute;
                  right: 0;
                  bottom: 0;
                  align-items: center;
                  justify-content: center;
                `}
                onPress={onEditPress}
              >
                <Icon name="pencil" size={rpx(12)} color="#ccc" />
              </TouchableOpacity>
            ) : null}
            <TouchableOpacity
              onPress={onDeletePress}
              css={`
                position: absolute;
                right: ${rpx(-7)}px;
                top: ${rpx(-7)}px;
                width: ${rpx(18)}px;
                height: ${rpx(18)}px;
                border-radius: ${rpx(9)}px;
                background-color: ${danger};
                align-items: center;
                justify-content: center;
              `}
            >
              <Icon name="close" size={rpx(15)} color="#fff" />
            </TouchableOpacity>
          </>
        )}
      </View>
    );
  },
);

const Row = memo(
  ({
    width,
    columns,
    editable,
    isFirstRow,
    cells,
    onCellPress,
    onDeleteMedia,
    onEditMedia,
    onRetry,
  }: {
    width: number;
    columns: number;
    editable?: boolean;
    isFirstRow?: boolean;
    cells: CellInfo[];
    onCellPress?: (cell: CellInfo) => void;
    onDeleteMedia?: (cell: CellInfo & { type: 'media' }) => void;
    onEditMedia?: (cell: CellInfo & { type: 'media' }) => void;
    onRetry?: (cell: CellInfo & { type: 'media' }) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const cellWidth = (width - rpx(kSpacing) * (columns - 1)) / columns;

    return (
      <View
        css={`
          flex-direction: row;
          flex-wrap: nowrap;
          align-items: stretch;
          justify-content: flex-start;
          margin-top: ${isFirstRow ? 2 : rpx(kVerticalSpacing)}px;
        `}
      >
        {cells.map((cell, index) => (
          <Cell
            key={cell.type === 'media' ? cell.media.key : '__placeholder__'}
            editable={editable}
            cell={cell}
            size={cellWidth}
            onPress={onCellPress}
            onDelete={onDeleteMedia}
            onEdit={onEditMedia}
            onRetry={onRetry}
            css={`
              margin-left: ${index === 0 ? 0 : rpx(kSpacing)}px;
            `}
          />
        ))}
      </View>
    );
  },
);

export const ItemMediaList = memo(
  ({
    editable,
    columns = kDefaultNumberOfColumns,
    style,
    medias,
    required,
    onTakeMedia,
    onDeleteMedia,
    onEditMedia,
    onRetryMediaUpload,
    onPreviewVideo,
  }: {
    medias: MediaInfo[];
    columns?: number;
    editable?: boolean;
    style?: StyleProp<ViewStyle>;
    required?: boolean;
    onTakeMedia?: () => void;
    onDeleteMedia?: (media: MediaInfo) => void;
    onEditMedia?: (media: MediaInfo) => void;
    onRetryMediaUpload?: (media: MediaInfo) => void;
    onPreviewVideo?: (media: MediaInfo) => void;
  }) => {
    const rows = useMemo(() => {
      const rowCount = editable
        ? Math.floor(medias.length / columns) + 1
        : Math.floor((medias.length - 1) / columns) + 1;
      const list = range(0, rowCount).map(i =>
        medias
          .slice(i * columns, (i + 1) * columns)
          .map<CellInfo>((media, j) => ({
            type: 'media',
            media,
            index: i * columns + j,
          })),
      );
      if (editable) {
        list[rowCount - 1].push({
          type: 'placeholder',
          showRequired: required && medias.length === 0,
        });
      }
      return list;
    }, [columns, editable, medias, required]);

    const [currentIndex, setCurrentIndex] = useState<number>();
    const images = useMemo(
      () => medias.filter(x => x.type === 'image') ?? [],
      [medias],
    );

    const imageUrls = useMemo(() => images.map(x => x.url), [images]);

    const currentImageIndex = useMemo(
      () =>
        currentIndex == null
          ? undefined
          : medias[currentIndex]?.type === 'image'
          ? images.findIndex(x => x.key === medias[currentIndex].key)
          : 0,
      [currentIndex, images, medias],
    );

    const onCellPress = useCallback(
      (cell: CellInfo) => {
        if (cell.type === 'placeholder') {
          onTakeMedia?.();
        } else {
          if (cell.media.type === 'image') {
            setCurrentIndex(cell.index);
          } else {
            onPreviewVideo?.(cell.media);
          }
        }
      },
      [onPreviewVideo, onTakeMedia],
    );

    const onDelete = useCallback(
      (cell: CellInfo & { type: 'media' }) => {
        onDeleteMedia?.(cell.media);
      },
      [onDeleteMedia],
    );

    const onEdit = useCallback(
      (cell: CellInfo & { type: 'media' }) => {
        onEditMedia?.(cell.media);
      },
      [onEditMedia],
    );

    const onRetry = useCallback(
      (cell: CellInfo & { type: 'media' }) => {
        onRetryMediaUpload?.(cell.media);
      },
      [onRetryMediaUpload],
    );

    return (
      <>
        <ImageViewer
          images={imageUrls}
          index={currentImageIndex}
          show={currentImageIndex != null}
          onDismiss={() => setCurrentIndex(undefined)}
        />
        <LayoutProviderView style={style}>
          {size =>
            rows.map((cells, k) => (
              <Row
                key={k}
                columns={columns}
                isFirstRow={k === 0}
                editable={editable}
                cells={cells}
                width={size.width}
                onCellPress={onCellPress}
                onDeleteMedia={onDelete}
                onEditMedia={onEdit}
                onRetry={onRetry}
              />
            ))
          }
        </LayoutProviderView>
      </>
    );
  },
);
